import MeasurementModel from "../models/MeasurementModel";
import TransactionService from "../services/TransactionService";
import AdvanceModel from "../models/AdvanceModel";
import InstallmentsLinkModel from "../models/InstallmentsLinkModel";
import AdvanceService from "../services/AdvanceService";
import MeasurementController from "./MeasurementController";
import { IAdvanceLink, IInstallmentsLink } from "../interfaces";
import { RequiredParamException } from "../exceptions/InvalidParamException";
import { isEmptyObject } from "../../utils";

class AdvanceController {
  createAdvance(contractUid: string, { code, title, date, installments }) {
    const advance = AdvanceModel.create("", {
      contractUid,
      code,
      title,
      date,
      installments,
    });
    return AdvanceService.createAdvance(advance);
  }

  updateAdvance(advance: AdvanceModel, updatedData) {
    const updatedDiscount = AdvanceModel.create(advance.uid, {
      contractUid: advance.contractUid,
      code: advance.code,
      date: updatedData.date,
      title: updatedData.title,
      installments: updatedData.installments,
    });
    return AdvanceService.updateAdvance(advance, updatedDiscount);
  }

  async linkAdvancesToMeasurement(
    measurement: MeasurementModel,
    advancesLinks: IInstallmentsLink<AdvanceModel>[]
  ): Promise<void> {
    if (!advancesLinks || !measurement) return;

    const measurementUid = measurement.uid;
    advancesLinks = advancesLinks.filter(
      (link) =>
        (link.target as AdvanceModel).contractUid === measurement.contractUid
    );

    if (isEmptyObject(advancesLinks))
      throw new RequiredParamException("advancesLinks");

    const convertedLinks = this.convertLinksToMeasurementLinks(advancesLinks);

    return TransactionService.run(async (transaction) => {
      await MeasurementController.linkAdvancesToMeasurement(
        transaction,
        measurement,
        convertedLinks
      );

      for (const advanceLink of advancesLinks)
        await this.addLinkToAdvance(
          transaction,
          advanceLink.target,
          advanceLink.installments,
          measurementUid
        );
    });
  }

  private convertLinksToMeasurementLinks(
    links: IInstallmentsLink<AdvanceModel>[]
  ): IAdvanceLink[] {
    return InstallmentsLinkModel.expandList(links).map((link) => ({
      advanceId: (link.target as AdvanceModel).uid,
      installment: link.installment,
      value: link.value,
    }));
  }

  private async addLinkToAdvance(
    transaction,
    advance: AdvanceModel,
    installments: number[],
    measurementUid: string
  ) {
    advance.removeInstallmentsMeasurement(measurementUid);
    advance.setInstallmentsMeasurement(installments, measurementUid);

    await AdvanceService.updateInstallmentsWithTransaction(
      transaction,
      advance
    );
  }
}

export default new AdvanceController();
